/**
 * Blog post seed data.
 *
 * Each post is a JS object so we don't need a markdown parser dependency
 * for the v1. Add a new entry to `posts` to publish a new article. Use the
 * `content` array of blocks — the renderer in `pages/BlogPost.js` maps each
 * block to a styled JSX element.
 *
 * Block types supported by the renderer:
 *   { type: 'p',     text: string }              – paragraph
 *   { type: 'h2',    text: string }              – section heading
 *   { type: 'h3',    text: string }              – sub-heading
 *   { type: 'ul',    items: string[] }           – bulleted list
 *   { type: 'ol',    items: string[] }           – numbered list
 *   { type: 'quote', text: string }              – pull-quote
 *   { type: 'cta',   text: string, href: string }– inline call-to-action
 */

export const posts = [
  {
    slug: 'why-we-built-interviewwala',
    title: 'Why we built InterviewWala',
    excerpt:
      'Most ATS products are built for HR teams hiring slowly. Indian staffing firms hire fast, in volume, across many clients. We built InterviewWala for them.',
    date: '2026-02-20',
    readTime: '4 min read',
    author: 'The InterviewWala team',
    cover: '/demo/01-dashboard.jpg',
    keywords:
      'applicant tracking system India, staffing firm software, recruitment tracking, ATS for agencies',
    content: [
      {
        type: 'p',
        text:
          'For three years, one of us ran recruitment for a staffing agency in Mumbai. Five recruiters, twelve active clients, 600+ candidates moving through the pipeline at any time. We tried every shape of tool — Zoho Recruit, a couple of HRMS suites, even a custom Excel + WhatsApp combo. Nothing fit.',
      },
      {
        type: 'p',
        text:
          'Most ATS products are built for in-house HR teams that hire slowly: 15-30 jobs a year, 1-2 candidates per role, the offer letter is the finish line. Staffing firms work the opposite way — dozens of jobs open at once, hundreds of candidates per role, the recruiter\'s job ends only when the candidate joins (and replacement guarantees mean the relationship continues for months).',
      },
      {
        type: 'h2',
        text: 'The three things that always broke',
      },
      {
        type: 'h3',
        text: '1. The pipeline was always five tabs deep',
      },
      {
        type: 'p',
        text:
          'In every product we used, "what should I do today?" required opening five different screens. Open jobs in one tab, candidate database in another, today\'s interviews in a third, last week\'s submissions in a fourth, the client portal in a fifth. By the time a recruiter found the candidate they wanted to follow up on, twenty minutes were gone.',
      },
      {
        type: 'h3',
        text: '2. Bulk uploads were never bulk enough',
      },
      {
        type: 'p',
        text:
          'A recruiter sourcing for an Engineering Manager role might collect 80 resumes from Naukri, LinkedIn and an internal referral spreadsheet — over a single afternoon. "Bulk upload" in most products meant one resume at a time, with manual fields. We needed real bulk: drag 80 PDFs, get 80 parsed candidates, no clicks per file.',
      },
      {
        type: 'h3',
        text: '3. The data was a black box once you grew past two recruiters',
      },
      {
        type: 'p',
        text:
          'Who screened this candidate first? Why did the client reject them last month? Did anyone follow up on the offer that was sent on Friday? Multi-recruiter firms need an audit trail by default — not as a "premium feature".',
      },
      {
        type: 'quote',
        text:
          '"I shortlisted 5 candidates in 30 seconds — used to take me half a day."',
      },
      {
        type: 'h2',
        text: 'What we built differently',
      },
      {
        type: 'ul',
        items: [
          'A single-screen Recruiter Command Center — open jobs on the left, drag-and-drop kanban in the middle, candidate detail on the right. No tab-switching.',
          'Drag 100+ PDFs at once. AI parses skills, experience, and contact info in seconds.',
          'Every stage transition is logged with the recruiter\'s name, timestamp, and remarks. No more "who moved this?".',
          'A "Best 10 candidates" button on every job — runs an AI shortlist against your candidate database in under 2 seconds.',
          'Free forever for solo recruiters. Pay only when your team grows past one user.',
        ],
      },
      {
        type: 'p',
        text:
          'The product has been in active use with 3 staffing teams in Mumbai and Bengaluru for the last four months. The overwhelming feedback: "It saves me half a day every week." That\'s why we\'re opening it up.',
      },
      {
        type: 'cta',
        text: 'Try InterviewWala free →',
        href: '/signup',
      },
    ],
  },

  {
    slug: '5-mistakes-staffing-firms-make-tracking-candidates-in-excel',
    title: '5 mistakes Indian staffing firms make tracking 1000+ candidates in Excel',
    excerpt:
      'Excel works for the first 100 candidates. By candidate 500 it starts costing you placements. Here\'s what to fix before it does.',
    date: '2026-02-15',
    readTime: '6 min read',
    author: 'The InterviewWala team',
    cover: '/demo/02-command-center.jpg',
    keywords:
      'staffing firm Excel mistakes, candidate tracking spreadsheet, recruitment Excel templates, ATS migration India',
    content: [
      {
        type: 'p',
        text:
          'Every Indian staffing firm starts with Excel. It\'s free, familiar, and works fine for the first hundred candidates. But somewhere between candidate 500 and 1500, the same spreadsheet that built the firm starts costing it placements. We\'ve seen the pattern in three different agencies. Here are the five mistakes that show up every time — and what to do about each one.',
      },
      {
        type: 'h2',
        text: '1. One file per recruiter, no source of truth',
      },
      {
        type: 'p',
        text:
          'The most common pattern: each recruiter maintains their own Excel for their clients. Hari\'s file is his "active 2026 list". Priya\'s file is "Q1 candidates v3 final". When a candidate from Hari\'s list applies for a job Priya is owning, nobody notices for three weeks — and by then the client has hired someone else.',
      },
      {
        type: 'p',
        text:
          'Fix: one shared candidate database, scoped per organisation. Every new candidate, regardless of who sourced them, becomes searchable to anyone in your firm immediately. Recruiters keep ownership of their own pipelines, but the candidate pool itself is shared.',
      },
      {
        type: 'h2',
        text: '2. The "stage" column is text, not a workflow',
      },
      {
        type: 'p',
        text:
          'In most Excel sheets, the stage column has values like "screened", "Screening", "Screening Done", "scrned", "screening (waiting client)" — all referring to the same state. Reporting becomes impossible because you cannot trust the data. "How many submissions did we send last week?" is a 30-minute manual exercise instead of a single dashboard read.',
      },
      {
        type: 'p',
        text:
          'Fix: a fixed stage taxonomy with strict transitions (Sourced → Screened → Shortlisted → Interview → Selected → Offered → Joined, with Rejected and On-hold as terminal states). Every change is logged with timestamp and the user who made it.',
      },
      {
        type: 'h2',
        text: '3. Resumes live in WhatsApp, Drive, Outlook — never the same place',
      },
      {
        type: 'p',
        text:
          'When a client asks "send me their resume again", the recruiter spends 20 minutes searching three places. Worse: when a recruiter quits, half their candidate resumes leave with them on their personal devices.',
      },
      {
        type: 'p',
        text:
          'Fix: every resume attached directly to the candidate record at upload time, stored centrally (not on personal devices), accessible via one URL. Bonus: an AI parser fills the candidate\'s skills and experience automatically so the recruiter doesn\'t retype data the resume already contains.',
      },
      {
        type: 'h2',
        text: '4. Zero memory of why a candidate was rejected before',
      },
      {
        type: 'p',
        text:
          'You source a candidate today, share them with the client, and the client says "we already rejected them six months ago — they failed the technical round". Your firm now looks unprofessional, and the client\'s trust takes a hit. The information existed somewhere — just not in any place a recruiter would naturally check before submitting.',
      },
      {
        type: 'p',
        text:
          'Fix: every candidate has a single timeline showing every stage they\'ve ever been in, every job they\'ve been considered for, and every remark left by any recruiter or client SPOC. Searchable. Permanent.',
      },
      {
        type: 'h2',
        text: '5. No clue about TAT, conversion, or recruiter performance',
      },
      {
        type: 'p',
        text:
          'How many days does the average candidate take to go from sourced to joined? Which recruiter has the highest interview-to-offer conversion? Which client takes 14 days to give feedback and which one takes 2? In Excel, none of this is calculable without a separate analysis project. Most firms just don\'t know.',
      },
      {
        type: 'p',
        text:
          'Fix: live metrics that update as the pipeline moves. Not a monthly export — a top-strip on every recruiter\'s home screen showing today\'s submissions, this month\'s closures, average TAT, and conversion percentage. The number tells the recruiter what to focus on next.',
      },
      {
        type: 'h2',
        text: 'When does it become urgent?',
      },
      {
        type: 'p',
        text:
          'Most staffing firms hit the wall around the 1000-candidate or third-recruiter mark — whichever comes first. By then, the cost of switching feels enormous because the data lives in fifteen Excel files. The trick is to plan the migration before you actually need it.',
      },
      {
        type: 'p',
        text:
          'A good ATS will let you import your existing Excel files in one shot — preserving names, contact info, skills, and even rejection reasons — so the team continues working without losing anything. The transition takes a weekend. The compounding benefit lasts years.',
      },
      {
        type: 'cta',
        text: 'See how it works — start free →',
        href: '/signup',
      },
    ],
  },
];

export const findPostBySlug = (slug) => posts.find((p) => p.slug === slug);
